import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import { bool, string } from 'prop-types';
import React, { Component, useRef, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { compose } from 'redux';

import { ensureCurrentUser } from '../../../util/data';
import { isUploadImageOverLimitError } from '../../../util/errors';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { getEncryptionText, getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import * as validators from '../../../util/validators';
import { Tooltip } from 'antd';
import {
  Avatar,
  Button,
  CustomExtendedDataField,
  FieldCheckbox,
  FieldDateInput,
  FieldLocationAutocompleteInput,
  FieldPhoneInput,
  FieldTextInput,
  Form,
  H4,
  IconSpinner,
  ImageFromFile,
  Modal,
  ValidationError
} from '../../../components';

import countryCodes from './countryCodes.json';

import { getBlobUrlFromBlobName, sendSMS } from '../../../util/api';
import css from './ProfileSettingsForm.module.css';
import defaultConfig from '../../../config/configDefault';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset

const identity = v => v;

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.state = { uploadDelay: false };
    this.submittedValues = {};
  }

  componentDidUpdate(prevProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        mutators={{ ...arrayMutators }}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            onUploadBlobFile,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            marketplaceName,
            values,
            userFields,
            userType,
            isAdmissionLetterBlobNameAvailable,
            ispassportBlobNameAvailable,
            isWhatsappPhoneNumberAvailable,
            isAddressAvailable,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);


          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });


          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid || pristine || pristineSinceLastSubmit || uploadInProgress || submitInProgress;

          const userFieldProps = getPropsForCustomUserFieldInputs(
            userFields,
            intl,
            userType,
            false
          );

          const passportPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.passportPlaceholder',
          });
          const phoneNumberVerifyPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.phoneNumberVerifyPlaceholder',
          });
          const addressRequiredMessage = intl.formatMessage({
            id: 'EditListingLocationForm.addressRequired',
          });

          // Verify phone number
          const generateOTP = () => {
            const otp = Math.floor(100000 + Math.random() * 900000);
            return otp.toString();
          };

          const [modelState, setModalSate] = useState({});
          const VerifyOtpComponent = () => {
            const phoneNumberAuth = useRef();

            const InitiateSMSVerification = () => {
              const sentOTP = generateOTP();

              const smsBody = intl.formatMessage(
                {
                  id: 'ProfileSettingsForm.phoneNumberOTPBody',
                },
                { otp: sentOTP }
              );

              setModalSate({ isOpen: true, sentOTP });
              const phoneNumber = form.getFieldState('phoneNumber').value?.trim();

              if (phoneNumber) {
                sendSMS({ body: getEncryptionText(smsBody, phoneNumber), to: phoneNumber }).then(res =>
                  console.log(res)
                );
              }
            };

            const validateSMSOtpVerification = () => {
              const otp = phoneNumberAuth.current.value;
              if (otp?.length === 6) {
                if (otp === modelState?.sentOTP) {
                  form.change('isPhoneNumberVerified', true);
                  setModalSate({});
                }
                else {
                  form.change('isPhoneNumberVerified', false);
                  setModalSate({
                    ...modelState,
                    errorMessage: 'ProfileSettingsForm.phoneNumberOTPIncorrect',
                  });
                }
              } else {
                setModalSate({
                  ...modelState,
                  errorMessage: 'ProfileSettingsForm.phoneNumberOTPInvalid',
                });
              }
            };

            return (
              <>
                <Button
                  className={css.verifyPhoneNumberButton}
                  onClick={InitiateSMSVerification}>
                  {intl.formatMessage({
                    id: 'ProfileSettingsForm.phoneNumberVerifyPhoneNumberLabel',
                  })}
                </Button>
                <Modal
                  id="phone-number-auth"
                  isOpen={modelState?.isOpen || false}
                  onClose={() => setModalSate({})}
                  onManageDisableScrolling={console.log}
                >
                  <FormattedMessage id="ProfileSettingsForm.phoneNumberVerify" />
                  <input
                    type="text"
                    maxLength={6}
                    onChange={e => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }}
                    ref={phoneNumberAuth}
                    placeholder={phoneNumberVerifyPlaceholder}
                  />
                  {!!modelState?.errorMessage && (
                    <ValidationError
                      fieldMeta={{
                        error: intl.formatMessage({ id: modelState?.errorMessage }),
                        touched: true,
                      }}
                    />
                  )}
                  <Button
                    key={`verify-otp-${phoneNumberAuth?.current?.value?.length}`}
                    className={css.phoneNumberVerifyField}
                    type="button"
                    onClick={validateSMSOtpVerification}
                  >
                    <FormattedMessage id="ProfileSettingsForm.phoneNumberVerifyLabel" />
                  </Button>
                </Modal>
              </>
            );
          };


          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              { isAddressAvailable ? null : 
              <>
                <FieldLocationAutocompleteInput
                  rootClassName={css.locationAddress}
                  inputClassName={css.locationAutocompleteInput}
                  // iconClassName={css.locationAutocompleteInputIcon}
                  predictionsClassName={css.predictionsRoot}
                  validClassName={css.validLocation}
                  // autoFocus={autoFocus}
                  name="Address"
                  // label={intl.formatMessage({ id: 'EditListingLocationForm.address' })}
                  label={intl.formatMessage({ id: 'ProfileSettingsForm.AddressLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'ProfileSettingsForm.AddressPlaceholder',
                  })}
                  useDefaultPredictions={false}
                  format={identity}
                  valueFromForm={values.Address}
                  validate={validators.composeValidators(
                    validators.autocompleteSearchRequired(addressRequiredMessage)
                    // validators.autocompletePlaceSelected(addressNotRecognizedMessage)
                  )}
                  hideIcon={true}
                  isdisplayAllCountries={true}
                /> <br/>
                </>
              }
              {isWhatsappPhoneNumberAvailable ? null :
                <div className={classNames(css.sectionContainer)}>
                  <FieldPhoneInput
                    className={css.phone}
                    name="whatsappPhoneNumber"
                    id={'whatsappPhoneNumber'}
                    label={intl.formatMessage({ id: 'ProfileSettingsForm.whatsappPhoneNumber' })}
                    placeholder={intl.formatMessage({
                      id: 'ProfileSettingsForm.whatsappPhoneNumber',
                    })}
                    defaultCountry="FR"
                    validate={validators.composeValidators(
                      validators.validatePhoneNumber(
                        intl.formatMessage({ id: 'ProfileSettingsForm.phoneNumberValidMessage' })
                      )
                    )}
                    onChange={e => {
                      form.change('whatsappPhoneNumber', e);
                    }}
                  />
                </div>
              }
              {ispassportBlobNameAvailable ? null :
                <div className={classNames(css.sectionContainer)}>
                  <H4 as="h2" className={css.sectionTitle}>
                    <FormattedMessage id="ProfileSettingsForm.passportHeading" />
                  </H4>
                  <Field
                    id="passportBlobName"
                    name="passportBlobName"
                    label={passportPlaceholder}
                    type="file"
                    form={null}
                  >
                    {fieldProps => {
                      const { id, input, label } = fieldProps;
                      const { name, type } = input;
                      const onChange = e => {
                        const file = e.target.files[0];
                        if (file != null) {
                          const tempId = `${file.name}_${Date.now()}`;
                          onUploadBlobFile({ id: tempId, file }).then(res => {
                            form.change(`passportBlobName`, res?.blobName);
                            form.change(`passportBlobUrl`, res?.blobUrl);
                            form.blur(`passportBlobName`);
                          });
                        }
                      };

                      const onPassportView = () => {
                        input?.value &&
                          getBlobUrlFromBlobName({ blobName: input?.value || '' }).then(res => {
                            window.open(res?.blobUrl, '_blank');
                          });
                      };

                      return (
                        <div>
                          {input?.value && (
                            <a
                              onClick={onPassportView}
                              className={[css.label, css.passportImagePlaceholder].join(' ')}
                            >
                              <Tooltip title={intl.formatMessage({ id: 'ProfileSettingsForm.viewHoverMessage' })} placement="right">
                                {intl.formatMessage({ id: 'ProfileSettingsForm.passportView' })}
                              </Tooltip>
                            </a>
                          )}
                          <div className={css.uploadAvatarWrapper}>
                            <label
                              href={input?.value}
                              className={[css.label, css.passportImagePlaceholder].join(' ')}
                              htmlFor={id}
                            >
                              {label}
                            </label>
                            <input
                              id={id}
                              name={name}
                              className={css.uploadAvatarInput}
                              onChange={onChange}
                              type={type}
                            />
                          </div>
                        </div>
                      );
                    }}
                  </Field>
                </div>
              }
              {isAdmissionLetterBlobNameAvailable ? null :
                <div className={classNames(css.sectionContainer)}>
                  <H4 as="h3" className={css.sectionTitle}>
                    <FormattedMessage id="ProfileSettingsForm.admissionLetterHeading" />
                  </H4>
                  <Field
                    id="admissionLetterBlobName"
                    name="admissionLetterBlobName"
                    label={intl.formatMessage({
                      id: 'ProfileSettingsForm.admissionLetterPlaceholder',
                    })}
                    type="file"
                    form={null}
                  >
                    {fieldProps => {
                      const { id, input, label } = fieldProps;
                      const { name, type } = input;
                      const onChange = e => {
                        const file = e.target.files[0];
                        if (file != null) {
                          const tempId = `${file.name}_${Date.now()}`;
                          onUploadBlobFile({ id: tempId, file }).then(res => {
                            form.change(`admissionLetterBlobName`, res?.blobName);
                            form.change(`admissionLetterBlobUrl`, res?.blobUrl);
                            form.blur(`admissionLetterBlobName`);
                          });
                        }
                      };

                      const onPassportView = () => {
                        input?.value &&
                          getBlobUrlFromBlobName({ blobName: input?.value || '' }).then(res => {
                            window.open(res?.blobUrl, '_blank');
                          });
                      };

                      return (
                        <div>
                          {input?.value && (
                            <a
                              onClick={onPassportView}
                              className={[css.label, css.passportImagePlaceholder].join(' ')}
                            >
                              <Tooltip title={intl.formatMessage({ id: 'ProfileSettingsForm.viewHoverMessage' })} placement="right">
                                {intl.formatMessage({
                                  id: 'ProfileSettingsForm.admissionLetterView',
                                })}
                              </Tooltip>
                            </a>
                          )}
                          <div className={css.uploadAvatarWrapper}>
                            <label
                              href={input?.value}
                              className={[css.label, css.passportImagePlaceholder].join(' ')}
                              htmlFor={id}
                            >
                              {label}
                            </label>
                            <input
                              id={id}
                              name={name}
                              className={css.uploadAvatarInput}
                              onChange={onChange}
                              type={type}
                            />
                          </div>
                        </div>
                      );
                    }}
                  </Field>
                </div>
              }
              {submitError}
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={pristineSinceLastSubmit}
              >
                <FormattedMessage id="ProfileSettingsForm.saveChanges" />
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
